import { useState, useCallback } from "react";
import {
  List,
  ActionIcon,
  Flex,
  Divider,
  Text,
  useMantineTheme,
  Box,
} from "@mantine/core";
import { IconPlaylistAdd } from "@tabler/icons";
import ActionCard from "src/components/ActionCard";
import { closeAllModals, useModals } from "@mantine/modals";
import { CardRadius, EMPLOYEE_ROLE_OBJ, HeightCard } from "src/utils/constants";
import {
  GetLocationOfferingListQuery,
  useGetLocationOfferingListQuery,
  useDeleteLocationOfferingMutation,
  useUpdateLocationOfferingMutation,
} from "src/generated/graphql";
import OfferingDetail from "./OfferingDetail";
import Switch from "src/components/Switch/Switch";
import useWritePermission from "src/hooks/usePermission";
import useTransitionMenu from "src/hooks/useTransitionMenu";

export type ILocationOfferingElement = NonNullable<
  GetLocationOfferingListQuery["locationOfferingMany"]
>[0];

type IOfferings = {
  locId: string;
};

function Offerings({ locId }: IOfferings) {
  const { TransitionMenu, setActiveOption } = useTransitionMenu();
  const isAllowWrite = useWritePermission(EMPLOYEE_ROLE_OBJ.orgLocations);
  const [savedAgreements, setsavedAgreements] = useState<string[]>([]);
  const modals = useModals();
  const theme = useMantineTheme();
  let color = theme.colors.secondaryTextColor[6];

  const { data: offerings, refetch } = useGetLocationOfferingListQuery({
    variables: {
      locationId: locId,
      isPromoCode: true,
    },
    skip: !locId,
    onCompleted(data) {
      if (data.locationOfferingMany) {
        setsavedAgreements(
          data.locationOfferingMany?.map((e) => e.agreement.id)
        );
      }
    },
  });

  const [deleteOffering] = useDeleteLocationOfferingMutation({
    onCompleted: () => {
      refetch();
      closeAllModals();
    },
  });

  const addActionIcon = (
    <ActionIcon disabled={!isAllowWrite} size="lg" onClick={() => onCreate()}>
      <IconPlaylistAdd color={color} />
    </ActionIcon>
  );

  const onCreate = useCallback(() => {
    modals.openModal({
      closeOnClickOutside: false,
      title: "Create Offering",
      children: (
        <OfferingDetail
          refetch={refetch}
          locationId={locId}
          savedAgreements={savedAgreements}
        />
      ),
    });
  }, [refetch, locId, savedAgreements]);

  const onEdit = useCallback(
    (offeringId: string, agreementId: string) => {
      const filterCurrentAgreementId = savedAgreements.filter(
        (e) => e !== agreementId
      );
      modals.openModal({
        closeOnClickOutside: false,
        title: `Edit Offering`,
        children: (
          <OfferingDetail
            refetch={refetch}
            locationId={locId}
            offeringId={offeringId}
            savedAgreements={filterCurrentAgreementId}
          />
        ),
      });
    },
    [refetch, locId, savedAgreements]
  );

  const onDelete = (id: string) => {
    deleteOffering({ variables: { deleteLocationOfferingId: id } });
  };

  const handleOpenListMenu = (option: ILocationOfferingElement) => {
    setActiveOption(option);
  };

  const [updateOffering] = useUpdateLocationOfferingMutation({
    onCompleted() {
      refetch();
    },
  });

  const handleUpdate = (id: string, isActive: boolean) => {
    updateOffering({ variables: { data: { isActive }, id } });
  };

  return (
    <ActionCard
      title="Membership Offerings"
      rightIconButton={isAllowWrite ? addActionIcon : null}
      height={HeightCard.md}
      radius={CardRadius.md}
      size={"md"}
      overflow="hidden"
    >
      <Box>
        {!offerings?.locationOfferingMany?.length ? (
          <Flex justify="center" align="center">
            <Text color={color}>Click</Text>&nbsp;{addActionIcon}&nbsp;
            <Text color={color}>to add a Location Offering</Text>
          </Flex>
        ) : (
          <List w="100%" listStyleType="none" style={{ color }}>
            {offerings?.locationOfferingMany?.map((item, index) => (
              <List.Item
                w="100%"
                icon={null}
                key={item.id}
                c={color}
                sx={{
                  "&": {
                    position: "relative",
                  },
                  "& ::marker": {
                    content: "none",
                  },
                  "& .mantine-List-itemWrapper": {
                    display: "flex",
                  },
                  "& .mantine-List-itemWrapper > span > div": {
                    alignItems: "center",
                  },
                }}
              >
                {!!index && (
                  <Divider style={{ borderColor: theme.colors.gray[2] }} />
                )}
                <Flex
                  align="center"
                  justify="space-between"
                  sx={{
                    padding: "4px 0",
                  }}
                >
                  <Text size={16} sx={{ width: "200px" }}>
                    {item.name}
                  </Text>

                  <Text fw={500} sx={{ width: "150px" }}>
                    {item.agreement?.name}
                  </Text>

                  {
                    <Switch
                      labelPosition="left"
                      label={"Active"}
                      checked={!!item.isActive}
                      disabled={!isAllowWrite}
                      onChange={() => handleUpdate(item.id, !item.isActive)}
                      mt="sm"
                    />
                  }
                  {isAllowWrite && (
                    <TransitionMenu
                      onClick={() => handleOpenListMenu(item)}
                      onEdit={() => onEdit(item.id, item.agreement.id)}
                      onDelete={onDelete}
                      id={item.id}
                    />
                  )}
                </Flex>
              </List.Item>
            ))}
          </List>
        )}
      </Box>
    </ActionCard>
  );
}

export default Offerings;
